/**
 * Tagalog Language Profile
 *
 * VSO word order, prepositions, space-separated.
 */

import type { LanguageProfile } from './types';

export const tagalogProfile: LanguageProfile = {
  code: 'tl',
  name: 'Tagalog',
  nativeName: 'Tagalog',
  direction: 'ltr',
  script: 'latin',
  wordOrder: 'VSO',
  markingStrategy: 'preposition',
  usesSpaces: true,
  defaultVerbForm: 'base',
  verb: {
    position: 'start',
    subjectDrop: true,
  },
  references: {
    me: 'ako', // "I/me"
    it: 'ito', // "it"
    you: 'ikaw', // "you"
    result: 'resulta', // "result"
    event: 'pangyayari', // "event"
    target: 'target', // "target"
    body: 'katawan', // was an English placeholder; the i18n dict emits the Tagalog word
    document: 'dokumento',
    window: 'bintana',
    detail: 'detalye',
  },
  possessive: {
    marker: 'ng', // Linker used in possessive constructions
    markerPosition: 'between',
    keywords: {
      // Tagalog uses postposed possessive pronouns
      // "my" - ko (clitic), akin (emphatic), aking (adjectival genitive)
      ko: 'me',
      akin: 'me',
      aking: 'me',
      // "your" - mo (clitic), iyo (emphatic)
      mo: 'you',
      iyo: 'you',
      // "its/his/her" - niya (clitic), kaniya (emphatic), nito (genitive proximal)
      niya: 'it',
      nito: 'it',
      kaniya: 'it',
    },
  },
  roleMarkers: {
    // `bago` (before) / `matapos` (after) are positional destination markers for
    // `put X before/after Y`, mirroring how Arabic lists قبل/بعد as destination
    // alternatives. Without them the generated put pattern can't match a
    // before/after target.
    destination: { primary: 'sa', alternatives: ['bago', 'matapos'], position: 'before' }, // "to/into"
    source: { primary: 'mula_sa', position: 'before' }, // "from"
    patient: { primary: '', position: 'before' },
    style: { primary: 'nang', position: 'before' }, // manner marker
  },
  keywords: {
    // Class/Attribute operations
    toggle: { primary: 'palitan', alternatives: ['itoggle'], normalized: 'toggle' },
    add: { primary: 'idagdag', alternatives: ['magdagdag'], normalized: 'add' },
    remove: { primary: 'alisin', alternatives: ['tanggalin'], normalized: 'remove' },
    // Content operations
    put: { primary: 'ilagay', alternatives: ['maglagay'], normalized: 'put' },
    append: { primary: 'idagdag_sa_dulo', normalized: 'append' },
    prepend: { primary: 'idagdag_sa_simula', normalized: 'prepend' },
    take: { primary: 'kumuha', normalized: 'take' },
    make: { primary: 'gumawa', alternatives: ['lumikha'], normalized: 'make' },
    clone: { primary: 'i-clone', normalized: 'clone' },
    swap: { primary: 'palitan_pwesto', alternatives: ['magpalit'], normalized: 'swap' },
    morph: { primary: 'baguhin', normalized: 'morph' },
    // Variable operations
    set: { primary: 'itakda', alternatives: ['magtakda'], normalized: 'set' },
    get: { primary: 'kunin', normalized: 'get' },
    increment: { primary: 'dagdagan', alternatives: ['taasan'], normalized: 'increment' },
    decrement: { primary: 'bawasan', alternatives: ['ibaba'], normalized: 'decrement' },
    log: { primary: 'itala', normalized: 'log' },
    // Visibility
    show: { primary: 'ipakita', alternatives: ['magpakita'], normalized: 'show' },
    hide: { primary: 'itago', alternatives: ['magtago'], normalized: 'hide' },
    transition: { primary: 'lumipat', normalized: 'transition' },
    // Events
    on: { primary: 'kapag', normalized: 'on' },
    trigger: { primary: 'palitawin', alternatives: ['magpatugtog'], normalized: 'trigger' },
    send: { primary: 'ipadala', alternatives: ['magpadala'], normalized: 'send' },
    // DOM focus
    focus: { primary: 'ituon', normalized: 'focus' },
    blur: { primary: 'alisin_tuon', normalized: 'blur' },
    // Phase 1 (v0.9.90): DOM / form state / debug
    empty: {
      primary: 'walang-laman',
      alternatives: ['alisin-laman', 'walang_laman'],
      normalized: 'empty',
    },
    open: { primary: 'buksan', normalized: 'open' },
    close: { primary: 'isara', normalized: 'close' },
    select: { primary: 'piliin', normalized: 'select' },
    clear: { primary: 'burahin', normalized: 'clear' },
    reset: { primary: 'i-reset', normalized: 'reset' },
    breakpoint: { primary: 'breakpoint', normalized: 'breakpoint' },
    // Navigation
    go: { primary: 'pumunta', alternatives: ['punta'], normalized: 'go' },
    // 'scroll' (English loanword) is the form the i18n transformer emits;
    // registered as an alternative for passthrough-alignment.
    scroll: { primary: 'iscroll', alternatives: ['mag_scroll', 'scroll'], normalized: 'scroll' },
    push: { primary: 'itulak', alternatives: ['ipush'], normalized: 'push' },
    replace: { primary: 'palitan_url', alternatives: ['ireplace'], normalized: 'replace' },
    process: { primary: 'iproseso', alternatives: ['proseso'], normalized: 'process' },
    // Async
    wait: { primary: 'maghintay', alternatives: ['hintay'], normalized: 'wait' },
    fetch: { primary: 'kuhanin_mula', alternatives: ['kunin_mula'], normalized: 'fetch' },
    settle: { primary: 'magpatahimik', normalized: 'settle' },
    // Control flow
    if: { primary: 'kung', normalized: 'if' },
    unless: { primary: 'maliban_kung', normalized: 'unless' },
    when: { primary: 'tuwing', normalized: 'when' },
    where: { primary: 'kung_saan', normalized: 'where' },
    else: { primary: 'kung_hindi', alternatives: ['kundi'], normalized: 'else' },
    repeat: { primary: 'ulitin', alternatives: ['paulit-ulit'], normalized: 'repeat' },
    for: { primary: 'para_sa', normalized: 'for' },
    while: { primary: 'habang', normalized: 'while' },
    // `repeat forever` loop keyword — corpus word recognized so loopType types
    // as `:literal` like EN (the repeat.loopType R1 residue; see spanish.ts).
    forever: { primary: 'magpakailanman', normalized: 'forever', alternatives: ['forever'] },
    continue: { primary: 'magpatuloy', normalized: 'continue' },
    halt: { primary: 'itigil', alternatives: ['huminto'], normalized: 'halt' },
    throw: { primary: 'ihagis', alternatives: ['itapon'], normalized: 'throw' },
    call: { primary: 'tawagin', alternatives: ['tawagan', 'tumawag'], normalized: 'call' },
    return: { primary: 'ibalik', alternatives: ['bumalik'], normalized: 'return' },
    then: { primary: 'pagkatapos', alternatives: ['saka'], normalized: 'then' },
    and: { primary: 'at', normalized: 'and' },
    // Comparison operator (`target matches .x`). Without this keyword the surface
    // stays an identifier and leaks verbatim into the condition's raw expression,
    // which the core expression parser reads as English (modal-close-backdrop /
    // focus-trap drop their then-branch). Not an ActionType and has no command
    // schema, so no pattern is generated from it.
    matches: { primary: 'tumutugma', normalized: 'matches' },
    // Copula (`if result is false`, `if my value is empty`). Without the keyword the
    // surface stays an identifier and leaks verbatim into the condition's raw
    // expression, which the core expression parser reads as English. Neither an
    // ActionType nor a command schema, so no pattern is generated from it.
    is: { primary: 'ay', normalized: 'is' },
    end: { primary: 'wakas', alternatives: ['tapos'], normalized: 'end' },
    // Advanced
    js: { primary: 'js', normalized: 'js' },
    // `sabay` is the form the i18n tl dictionary emits for async; register it so
    // the async-modifier strip recognizes it (passthrough-alignment).
    async: { primary: 'async', alternatives: ['sabay'], normalized: 'async' },
    tell: { primary: 'sabihin', alternatives: ['magsabi'], normalized: 'tell' },
    default: {
      primary: 'pamantayan',
      alternatives: ['default', 'unang_halaga'],
      normalized: 'default',
    },
    init: { primary: 'simulan', alternatives: ['magsimula'], normalized: 'init' },
    behavior: { primary: 'ugali', alternatives: ['kilos'], normalized: 'behavior' },
    install: { primary: 'ikabit', alternatives: ['mag-install'], normalized: 'install' },
    measure: { primary: 'sukatin', normalized: 'measure' },
    beep: { primary: 'tunog', normalized: 'beep' },
    break: { primary: 'putulin', normalized: 'break' },
    copy: { primary: 'kopyahin', normalized: 'copy' },
    exit: { primary: 'lumabas', normalized: 'exit' },
    pick: { primary: 'pumili', normalized: 'pick' },
    render: { primary: 'irender', normalized: 'render' },
    // Modifiers
    into: { primary: 'sa', normalized: 'into' },
    before: { primary: 'bago', normalized: 'before' },
    after: { primary: 'matapos', normalized: 'after' },
    // Event modifiers
    until: { primary: 'hanggang', normalized: 'until' },
    event: { primary: 'pangyayari', normalized: 'event' },
    from: { primary: 'mula', alternatives: ['galing'], normalized: 'from' },
    // Phase 8 (htmx v4 localized attributes): attribute-suffix keywords
    // used by the vocab generator — `sse-ikonekta`, `hx-live`, etc.
    // Filipino dev culture code-switches English heavily; `live`/`socket`
    // are typically written verbatim in code.
    connect: { primary: 'ikonekta', alternatives: ['kumonekta', 'connect'], normalized: 'connect' },
    stream: { primary: 'i-stream', alternatives: ['stream', 'agos'], normalized: 'stream' },
    live: { primary: 'live', alternatives: ['tuwiran', 'direkta'], normalized: 'live' },
    socket: { primary: 'socket', alternatives: ['websocket'], normalized: 'socket' },
    // Streaming/concurrency: transformer emits the English literal (no i18n dict
    // entry yet); Filipino code-switches English heavily, so English primary.
    worker: { primary: 'worker', alternatives: ['manggagawa'], normalized: 'worker' },
    eventsource: { primary: 'eventsource', normalized: 'eventsource' },
    // Service-worker keyword (Filipino code-switches English heavily; English
    // primary + native alternative, matching the socket/live entries above)
    intercept: {
      primary: 'intercept',
      alternatives: ['harangin', 'humarang'],
      normalized: 'intercept',
    },
    // Reactive: bind (generated text keeps the English verb; native primary
    // for future i18n dictionary support)
    bind: { primary: 'itali', alternatives: ['iugnay', 'bind'], normalized: 'bind' },
  },
  eventHandler: {
    keyword: { primary: 'kapag', normalized: 'on' },
    sourceMarker: { primary: 'mula_sa', alternatives: ['galing_sa'], position: 'before' },
    eventMarker: { primary: 'kapag', position: 'before' },
  },
};
